/**
 * チバリヨ - P4 アーカイブ / 復活モーダル (Phase 2-A-3)
 *
 * 公開API:
 *   - openArchiveModal({ client, currentSessionMember, onComplete })
 *       顧客をアーカイブする(active=false 化)
 *   - openUnarchiveModal({ client, currentSessionMember, onComplete })
 *       アーカイブ済み顧客を復活させる(active=true 化、archive 履歴は保持)
 *
 * 動作:
 *   - 単一の overlay/panel を共通利用、モードで分岐
 *   - 理由入力は任意(空欄でも可)、推奨タグチップ付き
 *   - 実行中はボタン disable、二重押し防止
 *   - 完了時は onComplete() を呼び出して呼び元(P4)が一覧を再読込
 *
 * Firestore 更新:
 *   - archive:
 *       active: false, archivedAt, archivedBy, archivedReason
 *       unarchived* は null クリア(再アーカイブ時に古い履歴が残らないように)
 *   - unarchive:
 *       active: true, unarchivedAt, unarchivedBy, unarchivedReason
 *       archivedAt/By/Reason は保持(監査痕跡)
 */

import {
  doc,
  setDoc,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { db } from './firebase-init.js';

// アーカイブ理由の推奨タグ
const ARCHIVE_REASON_TAGS = [
  '取引終了',
  '統合済み',
  '重複',
  '長期休眠',
  '誤登録',
  'その他'
];

// 復活理由の推奨タグ
const UNARCHIVE_REASON_TAGS = [
  '取引再開',
  '誤アーカイブ',
  '情報更新',
  'その他'
];

// =====================================================================
// モーダル状態
// =====================================================================
let modalState = null;

// =====================================================================
// 公開API
// =====================================================================
export function openArchiveModal({ client, currentSessionMember, onComplete }) {
  if (!client || !client.id) {
    console.warn('[チバリヨ P4 archive] client が不正です');
    return;
  }
  modalState = {
    mode: 'archive',
    client,
    currentSessionMember,
    onComplete,
    reason: '',
    selectedTags: new Set(),
    saving: false
  };
  renderModal();
}

export function openUnarchiveModal({ client, currentSessionMember, onComplete }) {
  if (!client || !client.id) {
    console.warn('[チバリヨ P4 unarchive] client が不正です');
    return;
  }
  modalState = {
    mode: 'unarchive',
    client,
    currentSessionMember,
    onComplete,
    reason: '',
    selectedTags: new Set(),
    saving: false
  };
  renderModal();
}

// =====================================================================
// モーダル描画
// =====================================================================
function renderModal() {
  // 既存のモーダル DOM があれば消す(state は保持)
  removeOverlayDom();

  const overlay = document.createElement('div');
  overlay.className = 'p4-arch-overlay';
  overlay.id = 'p4-arch-overlay';

  const isArchive = modalState.mode === 'archive';

  overlay.innerHTML = `
    <div class="p4-arch-modal ${isArchive ? 'is-archive' : 'is-unarchive'}" role="dialog" aria-modal="true">
      <div class="p4-arch-header">
        <h2 class="p4-arch-title">
          ${isArchive ? '顧客をアーカイブ' : '顧客を復活'}
        </h2>
        <button type="button" class="p4-arch-close" id="p4-arch-close-x" aria-label="閉じる">×</button>
      </div>

      <div class="p4-arch-body" id="p4-arch-body">
        ${renderBody()}
      </div>

      <div class="p4-arch-status" id="p4-arch-status"></div>

      <div class="p4-arch-footer">
        <button type="button" class="btn btn-ghost" id="p4-arch-cancel-btn">キャンセル</button>
        <button type="button"
                class="btn ${isArchive ? 'btn-danger' : 'btn-primary'}"
                id="p4-arch-execute-btn">
          ${isArchive ? 'アーカイブする' : '復活する'}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  attachEvents();
}

function renderBody() {
  const isArchive = modalState.mode === 'archive';
  const c = modalState.client;
  const name = c.name || '(名前未設定)';
  const tags = isArchive ? ARCHIVE_REASON_TAGS : UNARCHIVE_REASON_TAGS;

  // 復活時はアーカイブ履歴を表示
  const archiveInfoBlock = !isArchive ? `
    <div class="p4-arch-info-block">
      <div class="p4-arch-info-title">アーカイブ情報</div>
      <dl class="p4-arch-info-list">
        ${c.archivedAt ? `
          <dt>アーカイブ日</dt>
          <dd>${formatTimestamp(c.archivedAt)}</dd>
        ` : ''}
        ${c.archivedReason ? `
          <dt>アーカイブ理由</dt>
          <dd>${escapeHtml(c.archivedReason)}</dd>
        ` : `
          <dt>アーカイブ理由</dt>
          <dd class="p4-arch-muted">(記録なし)</dd>
        `}
      </dl>
    </div>
  ` : '';

  // 説明文
  const description = isArchive ? `
    <p class="p4-arch-desc">
      <strong>${escapeHtml(name)}</strong> をアーカイブします。<br>
      アーカイブ済み顧客は一覧の「アーカイブ」セグメントに移動し、編集できなくなります。<br>
      後で復活させることも可能です。
    </p>
  ` : `
    <p class="p4-arch-desc">
      <strong>${escapeHtml(name)}</strong> を復活させます。<br>
      復活後は通常の顧客として編集可能になります。
    </p>
  `;

  return `
    ${description}
    ${archiveInfoBlock}

    <div class="p4-arch-section">
      <label class="p4-arch-label">
        ${isArchive ? 'アーカイブ理由' : '復活理由'}
        <span class="p4-arch-optional">(任意)</span>
      </label>

      <div class="p4-arch-tags">
        ${tags.map((t) => `
          <button type="button"
                  class="p4-arch-tag"
                  data-tag="${escapeAttr(t)}">
            ${escapeHtml(t)}
          </button>
        `).join('')}
      </div>

      <textarea id="p4-arch-reason-input"
                class="p4-arch-textarea"
                rows="3"
                maxlength="500"
                placeholder="${isArchive ? 'アーカイブ理由を自由記述(任意)' : '復活理由を自由記述(任意)'}"></textarea>
    </div>
  `;
}

// =====================================================================
// イベントバインド
// =====================================================================
function attachEvents() {
  const overlay = document.getElementById('p4-arch-overlay');
  if (!overlay) return;

  // 閉じる
  document.getElementById('p4-arch-close-x').addEventListener('click', handleClose);
  document.getElementById('p4-arch-cancel-btn').addEventListener('click', handleClose);

  // オーバーレイ外側クリックで閉じる
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) handleClose();
  });

  // ESC キーで閉じる
  document.addEventListener('keydown', handleEscKey);

  // タグチップ
  overlay.querySelectorAll('.p4-arch-tag').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tag = btn.dataset.tag;
      if (modalState.selectedTags.has(tag)) {
        modalState.selectedTags.delete(tag);
        btn.classList.remove('is-selected');
      } else {
        modalState.selectedTags.add(tag);
        btn.classList.add('is-selected');
      }
      syncReasonFromTags();
    });
  });

  // 理由入力(手動編集を優先、タグからの自動挿入と共存)
  const textarea = document.getElementById('p4-arch-reason-input');
  textarea.addEventListener('input', (e) => {
    modalState.reason = e.target.value;
  });

  // 実行ボタン
  document.getElementById('p4-arch-execute-btn').addEventListener('click', executeAction);
}

function handleEscKey(e) {
  if (e.key === 'Escape') {
    handleClose();
  }
}

function handleClose() {
  if (modalState && modalState.saving) {
    // 保存中はキャンセル不可
    return;
  }
  closeOverlayElement();
}

function closeOverlayElement() {
  document.removeEventListener('keydown', handleEscKey);
  removeOverlayDom();
  modalState = null;
}

// DOM のみクリーンアップ(state は触らない、renderModal の preventive cleanup 用)
function removeOverlayDom() {
  const overlay = document.getElementById('p4-arch-overlay');
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
}

// タグの選択状態を理由欄に反映
//   - タグからの自動挿入: 選択タグを「、」連結
//   - ユーザが手で書いた内容は尊重したいが、ここではシンプルに上書きする
//     (タグ操作後に手編集すれば最終的にその内容が保存される)
function syncReasonFromTags() {
  const textarea = document.getElementById('p4-arch-reason-input');
  if (!textarea) return;

  const tagsStr = [...modalState.selectedTags].join('、');
  textarea.value = tagsStr;
  modalState.reason = tagsStr;
}

// =====================================================================
// 実行
// =====================================================================
async function executeAction() {
  if (!modalState || modalState.saving) return;

  modalState.saving = true;
  const isArchive = modalState.mode === 'archive';
  const c = modalState.client;
  const reason = (modalState.reason || '').trim();
  const memberId = modalState.currentSessionMember?.id || null;

  // ボタン無効化
  setButtonsDisabled(true);
  setStatus(isArchive ? 'アーカイブ処理中…' : '復活処理中…', 'info');

  try {
    let payload;
    if (isArchive) {
      payload = {
        active: false,
        archivedAt: serverTimestamp(),
        archivedBy: memberId,
        archivedReason: reason,
        // 再アーカイブ時、前回の unarchive 履歴は意味を失うのでクリア
        unarchivedAt: null,
        unarchivedBy: null,
        unarchivedReason: null,
        updatedAt: serverTimestamp()
      };
    } else {
      payload = {
        active: true,
        // archivedAt/By/Reason は監査痕跡として保持(クリアしない)
        unarchivedAt: serverTimestamp(),
        unarchivedBy: memberId,
        unarchivedReason: reason,
        updatedAt: serverTimestamp()
      };
    }

    await setDoc(doc(db, 'clients', c.id), payload, { merge: true });

    setStatus(isArchive ? 'アーカイブしました' : '復活しました', 'success');

    // 少し待ってから閉じる(ユーザが結果を読める時間を確保)
    setTimeout(() => {
      const cb = modalState?.onComplete;
      closeOverlayElement();
      if (typeof cb === 'function') cb();
    }, 600);

  } catch (err) {
    console.error('[チバリヨ P4 archive] 実行失敗:', err);
    setStatus(
      (isArchive ? 'アーカイブに失敗しました: ' : '復活に失敗しました: ') + err.message,
      'error'
    );
    modalState.saving = false;
    setButtonsDisabled(false);
  }
}

function setButtonsDisabled(disabled) {
  const cancelBtn = document.getElementById('p4-arch-cancel-btn');
  const execBtn = document.getElementById('p4-arch-execute-btn');
  const closeX = document.getElementById('p4-arch-close-x');
  if (cancelBtn) cancelBtn.disabled = disabled;
  if (execBtn) execBtn.disabled = disabled;
  if (closeX) closeX.disabled = disabled;
}

function setStatus(msg, level) {
  const el = document.getElementById('p4-arch-status');
  if (!el) return;
  el.textContent = msg;
  el.className = 'p4-arch-status is-' + (level || 'info');
}

// =====================================================================
// ユーティリティ
// =====================================================================
function formatTimestamp(ts) {
  if (!ts) return '(不明)';
  let date;
  if (typeof ts.toDate === 'function') {
    date = ts.toDate();
  } else if (ts.seconds) {
    date = new Date(ts.seconds * 1000);
  } else {
    date = new Date(ts);
  }
  if (isNaN(date.getTime())) return '(不明)';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

function escapeAttr(str) {
  return escapeHtml(str);
}
